import React, { useState } from 'react';
import { ASSESSMENT_QUESTIONS } from '../constants';
import { Award, RefreshCcw, Printer } from 'lucide-react';

interface AssessmentProps {
  onComplete: (score: number) => void;
  previousScore: number | null;
}

export const Assessment: React.FC<AssessmentProps> = ({ onComplete, previousScore }) => {
  const [currentQIndex, setCurrentQIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState<number[]>(new Array(ASSESSMENT_QUESTIONS.length).fill(-1));
  const [finished, setFinished] = useState(false);
  const [score, setScore] = useState(0);

  const handleAnswer = (optionIndex: number) => {
    const newAnswers = [...userAnswers];
    newAnswers[currentQIndex] = optionIndex;
    setUserAnswers(newAnswers);
  };

  const submit = () => {
    let correctCount = 0;
    userAnswers.forEach((ans, idx) => {
      if (ans === ASSESSMENT_QUESTIONS[idx].correctIndex) correctCount++;
    });
    const finalScore = Math.round((correctCount / ASSESSMENT_QUESTIONS.length) * 100);
    setScore(finalScore);
    onComplete(finalScore);
    setFinished(true);
  };

  if (finished) {
    return (
      <div className="max-w-2xl mx-auto p-8 text-center bg-white rounded-xl shadow-xl mt-10">
        <Award className={`w-24 h-24 mx-auto mb-6 ${score >= 70 ? 'text-yellow-400' : 'text-slate-300'}`} />
        <h2 className="text-3xl font-bold mb-4">Assessment Complete</h2>
        <p className="text-xl mb-6">You scored <span className={`font-bold ${score >= 70 ? 'text-green-600' : 'text-red-500'}`}>{score}%</span></p>
        
        {score >= 70 ? (
          <div className="bg-green-50 p-6 rounded-lg border border-green-200 mb-6">
            <h3 className="text-xl font-bold text-green-800 mb-2">Certificate of Completion</h3>
            <p className="mb-4">Awarded to Learner for demonstrating competency in Malware Basics.</p>
            <button onClick={() => window.print()} className="flex items-center justify-center gap-2 mx-auto bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700">
              <Printer size={18} /> Print Certificate
            </button>
          </div>
        ) : (
          <div className="mb-6">
            <p className="text-slate-600 mb-4">You need 70% to pass. Review the modules and try again.</p>
            <button 
              onClick={() => { setFinished(false); setCurrentQIndex(0); setUserAnswers(new Array(ASSESSMENT_QUESTIONS.length).fill(-1)); }}
              className="flex items-center justify-center gap-2 mx-auto bg-cyber-accent text-white px-6 py-3 rounded-full"
            >
              <RefreshCcw size={18} /> Retry
            </button>
          </div>
        )}
      </div>
    );
  }

  const question = ASSESSMENT_QUESTIONS[currentQIndex];

  return (
    <div className="max-w-3xl mx-auto p-6 md:p-12">
      <div className="bg-white p-8 rounded-2xl shadow-lg">
        <div className="flex justify-between items-center mb-6">
          <span className="text-slate-500 font-bold uppercase tracking-wider text-sm">Question {currentQIndex + 1} of {ASSESSMENT_QUESTIONS.length}</span>
          <span className="text-cyber-accent font-bold">{Math.round(((currentQIndex + 1) / ASSESSMENT_QUESTIONS.length) * 100)}%</span>
        </div>
        
        <h3 className="text-2xl font-bold mb-8">{question.text}</h3>
        
        <div className="space-y-3 mb-8">
          {question.options.map((opt, idx) => (
            <button
              key={idx}
              onClick={() => handleAnswer(idx)}
              className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                userAnswers[currentQIndex] === idx 
                  ? 'border-cyber-accent bg-blue-50 text-cyber-accent font-bold' 
                  : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
              }`}
            >
              {opt}
            </button>
          ))}
        </div>

        <div className="flex justify-between">
          <button 
            onClick={() => setCurrentQIndex(Math.max(0, currentQIndex - 1))}
            disabled={currentQIndex === 0}
            className="text-slate-500 font-bold disabled:opacity-30"
          >
            Previous
          </button>
          
          {currentQIndex < ASSESSMENT_QUESTIONS.length - 1 ? (
            <button 
              onClick={() => setCurrentQIndex(currentQIndex + 1)}
              className="bg-cyber-dark text-white px-6 py-2 rounded-lg font-bold hover:bg-slate-800"
            >
              Next
            </button>
          ) : (
            <button 
              onClick={submit}
              disabled={userAnswers.includes(-1)}
              className="bg-green-600 text-white px-8 py-2 rounded-lg font-bold hover:bg-green-700 disabled:opacity-50"
            >
              Submit Exam
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
